import { FC, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel, GridPaginationModel, GridRowId } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { jaJP } from "@mui/x-data-grid/locales";
import { formatDate } from "../../../utils/datetimeUtils";
import { downloadMultipleImagesZip } from "../../../utils/downloadUtils";
import PageContainer from "../../../components/pageContainer";
import useReport from "../../../hooks/useReport";
import { ReportMinimalResponse } from "../../../client";
import {
  FilterListOutlined,
  MoreVert,
  GetApp,
  CheckBoxOutlineBlank,
  CheckBox,
  IndeterminateCheckBox,
  Restore
} from "@mui/icons-material";
import { PATHS } from "../../../routes/paths";
import ReportFilterDialog, { FilterValues } from "../../../components/reportFilterDialog";

interface ActionMenuProps {
  params: GridRenderCellParams;
  onDelete: () => void;
  onRestore: () => void;
  onError: (message: string) => void;
  isArchived: boolean;
}

const ActionMenu: FC<ActionMenuProps> = ({ params, onDelete, onRestore, onError, isArchived }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const { deleteReport, restoreReport } = useReport();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const reportId = typeof params.id === 'string' ? parseInt(params.id, 10) : params.id;
      const success = await deleteReport(reportId);
      if (success) {
        onDelete();
      }
      setOpenDeleteDialog(false);
    } catch (error: any) {
      onError(error.message || "削除処理中にエラーが発生しました");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      const reportId = typeof params.id === 'string' ? parseInt(params.id, 10) : params.id;
      const success = await restoreReport(reportId);
      if (success) {
        onRestore();
      }
      setOpenRestoreDialog(false);
    } catch (error: any) {
      onError(error.message || "復元処理中にエラーが発生しました");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={`menu-${params.id}`}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={`menu-${params.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => {
          handleClose();
          navigate(
            `${PATHS.REPORT_FREE_DETAIL.replaceAll(
              ":id",
              params.id.toString()
            )}`
          );
        }}>詳細</MenuItem>
        {isArchived ? (
          <MenuItem
            onClick={() => {
              handleClose();
              setOpenRestoreDialog(true);
            }}
            sx={{ color: 'success.main' }}
          >
            復元
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleClose();
              setOpenDeleteDialog(true);
            }}
            sx={{ color: 'error.main' }}
          >
            削除
          </MenuItem>
        )}
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !isDeleting && setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>本当に削除しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setOpenDeleteDialog(false)}
            disabled={isDeleting}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 復元確認ダイアログ */}
      <Dialog
        open={openRestoreDialog}
        onClose={() => !isRestoring && setOpenRestoreDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>復元確認</DialogTitle>
        <DialogContent>
          <Typography>この報告書を復元しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setOpenRestoreDialog(false)}
            disabled={isRestoring}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleRestore}
            disabled={isRestoring}
          >
            復元
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ReportFreeList: FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportMinimalResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const { getReportListByType, restoreReport } = useReport();
  const [openRefineDialog, setOpenRefineDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // アーカイブモードの状態
  const [isArchivedMode, setIsArchivedMode] = useState(false);

  // 拡張された絞り込み条件
  const [filterValues, setFilterValues] = useState<FilterValues>({
    deliveryDestination: "",
    address: "",
    workResult: "",
    responsiblePerson: "",
    workerName: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 現在のページに表示されているデータを取得
  const getCurrentPageData = () => {
    const startIndex = paginationModel.page * paginationModel.pageSize;
    const endIndex = startIndex + paginationModel.pageSize;
    return reports.slice(startIndex, endIndex);
  };

  // 現在のページの選択状態を取得
  const getCurrentPageSelectionState = () => {
    const currentPageData = getCurrentPageData();
    const currentPageIds = currentPageData.map(item => item.id);
    const selectedCurrentPageIds = selectionModel.filter(id => {
      const normalizedId = typeof id === 'string' ? parseInt(id, 10) : id;
      return currentPageIds.includes(normalizedId);
    });

    if (selectedCurrentPageIds.length === 0) {
      return 'none';
    } else if (selectedCurrentPageIds.length === currentPageIds.length) {
      return 'all';
    } else {
      return 'some';
    }
  };

  // 行選択のハンドリング関数
  const handleRowSelection = (id: GridRowId) => {
    setSelectionModel(prev => {
      const normalizedId = typeof id === 'string' ? parseInt(id, 10) : id;
      const isSelected = prev.some(selectedId => {
        const normalizedSelectedId = typeof selectedId === 'string' ? parseInt(selectedId, 10) : selectedId;
        return normalizedSelectedId === normalizedId;
      });

      if (isSelected) {
        // 選択解除
        return prev.filter(selectedId => {
          const normalizedSelectedId = typeof selectedId === 'string' ? parseInt(selectedId, 10) : selectedId;
          return normalizedSelectedId !== normalizedId;
        });
      } else {
        // 選択追加
        return [...prev, id];
      }
    });
  };

  // 現在のページの全選択/全選択解除
  const handleTogglePageSelection = () => {
    const currentPageData = getCurrentPageData();
    const currentPageIds = currentPageData.map(item => item.id);
    const selectionState = getCurrentPageSelectionState();

    if (selectionState === 'all') {
      // 全選択解除：現在のページのIDを選択から除外
      setSelectionModel(prev => prev.filter(id => {
        const normalizedId = typeof id === 'string' ? parseInt(id, 10) : id;
        return !currentPageIds.includes(normalizedId);
      }));
    } else {
      // 全選択：現在のページのIDを選択に追加（重複を避ける）
      setSelectionModel(prev => {
        const newSelection = [...prev];
        currentPageIds.forEach(id => {
          const existingId = newSelection.find(selId => {
            const normalizedSelId = typeof selId === 'string' ? parseInt(selId, 10) : selId;
            return normalizedSelId === id;
          });
          if (!existingId) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  // 検索・絞り込み実行関数
  const handleSearch = useCallback(async (search: string, filters?: typeof filterValues, archived?: boolean) => {
    setLoading(true);
    try {
      const currentFilters = filters || filterValues;
      const archiveMode = archived !== undefined ? archived : isArchivedMode;
      const data = await getReportListByType({
        reportType: 3,
        search: search.trim() || null,
        deliveryDestination: currentFilters.deliveryDestination || null,
        address: currentFilters.address || null,
        workResult: currentFilters.workResult || null,
        responsiblePerson: currentFilters.responsiblePerson || null,
        workerName: currentFilters.workerName || null,
        status: currentFilters.status || null,
        startDate: currentFilters.startDate || null,
        endDate: currentFilters.endDate || null,
        archived: archiveMode,
      });
      if (data) {
        setReports(data);
        // 検索後は選択をクリア
        setSelectionModel([]);
        // ページを最初に戻す
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error: any) {
      setErrorMessage(error.message || "検索中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [filterValues, isArchivedMode, getReportListByType]);

  // アーカイブモード変更時の処理
  const handleArchivedModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newArchivedMode = event.target.checked;
    setIsArchivedMode(newArchivedMode);
    setSelectionModel([]); // 選択をクリア
    setPaginationModel(prev => ({ ...prev, page: 0 })); // ページをリセット
    // 新しいモードで検索実行
    handleSearch(searchTerm, filterValues, newArchivedMode);
  };

  // 初回読み込み時
  useEffect(() => {
    handleSearch("", filterValues, false);
  }, []);

  // 検索フォームのEnterキーハンドラー
  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch(searchTerm);
    }
  };

  // 絞り込み適用時の処理
  const handleFilter = async () => {
    await handleSearch(searchTerm, filterValues);
    setOpenRefineDialog(false);
  };

  // 絞り込み条件のクリア
  const handleClearFilter = () => {
    const clearedFilters: FilterValues = {
      deliveryDestination: "",
      address: "",
      workResult: "",
      responsiblePerson: "",
      workerName: "",
      status: "",
      startDate: "",
      endDate: "",
    };
    setFilterValues(clearedFilters);
  };

  // 絞り込み条件がアクティブかどうかを判定
  const hasActiveFilters = Object.values(filterValues).some(value => value !== "");

  // 複数選択された報告書の画像をZIPダウンロード
  const handleDownloadSelectedImages = async () => {
    if (selectionModel.length === 0) {
      setErrorMessage("ダウンロード対象を選択してください");
      return;
    }

    const selectedIds = selectionModel.map(id =>
      typeof id === 'string' ? parseInt(id, 10) : id
    );

    await downloadMultipleImagesZip(
      {
        ids: selectedIds,
        reportType: 3, // フリー報告書
        setLoading: setDownloadLoading,
        onSuccess: () => {
          setSuccessMessage(`${selectionModel.length}件の報告書の画像をダウンロードしました`);
          setSelectionModel([]);
        },
        onError: (error) => {
          setErrorMessage(error.message);
        }
      },
      'report'
    );
  };

  const handleDelete = useCallback(() => {
    handleSearch(searchTerm);
    setSuccessMessage("報告書を削除しました");
  }, [handleSearch, searchTerm]);

  const handleRestore = useCallback(() => {
    handleSearch(searchTerm);
    setSuccessMessage("報告書を復元しました");
  }, [handleSearch, searchTerm]);

  const handleDeleteError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  // アーカイブボタンのチェックボックスアイコンを取得
  const getArchiveIcon = () => {
    return isArchivedMode ? <CheckBox /> : <CheckBoxOutlineBlank />;
  };

  // 選択状態に応じたアイコンを取得
  const getSelectionIcon = () => {
    const state = getCurrentPageSelectionState();
    switch (state) {
      case 'all':
        return <CheckBox />;
      case 'some':
        return <IndeterminateCheckBox />;
      default:
        return <CheckBoxOutlineBlank />;
    }
  };

  // 選択状態に応じたボタンテキストを取得
  const getSelectionButtonText = () => {
    const state = getCurrentPageSelectionState();

    switch (state) {
      case 'all':
        return `全選択解除`;
      case 'some':
        return `全選択`;
      default:
        return `全選択`;
    }
  };

  // カスタムチェックボックス列を含む列定義
  const listColumns: GridColDef[] = [
    // アーカイブモードでない場合のみチェックボックス列を表示
    ...(!isArchivedMode ? [{
      field: "checkbox",
      headerName: "",
      width: 58,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const isSelected = selectionModel.some(selectedId => {
          const normalizedSelectedId = typeof selectedId === 'string' ? parseInt(selectedId, 10) : selectedId;
          const normalizedParamsId = typeof params.id === 'string' ? parseInt(params.id, 10) : params.id;
          return normalizedSelectedId === normalizedParamsId;
        });

        return (
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(event) => {
              event.stopPropagation();
              handleRowSelection(params.id);
            }}
          />
        );
      },
    }] : []),
    {
      field: "delivery_destination",
      headerName: "納入先",
      flex: 2.2,
    },
    {
      field: "start_date",
      headerName: "開始日",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <>{formatDate(params.value)}</>,
    },
    ...(isArchivedMode ? [{
      field: "deleted_at",
      headerName: "削除日",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <>{formatDate(params.value)}</>,
    }] : []),
    {
      field: "work_result",
      headerName: "結果",
      flex: 0.65,
      renderCell: (params: GridRenderCellParams) => {
        const label = params.value;
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Chip
              label={label}
              size="small"
              color={label === "完了" ? "success" : "warning"}
            />
          </Box>
        );
      },
    },
    {
      field: "responsible_person",
      headerName: "責任者",
      minWidth: 50,
      flex: 0.7,
    },
    {
      field: "creator_name",
      headerName: "作成者",
      minWidth: 80,
      flex: 0.8,
    },
    ...(!isArchivedMode ? [{
      field: "sales_status",
      headerName: "営業",
      minWidth: 100,
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => {
        const label = params.value as string;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Chip
              label={label}
              size="small"
              color={label === "対応済み" ? "success" : "warning"}
              variant={label === "対応済み" ? "filled" : "outlined"}
            />
          </Box>
        );
      },
    }, {
      field: "status",
      headerName: "ステータス",
      minWidth: 100,
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => {
        const label = params.value;
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Chip
              label={label}
              size="small"
              color={label === "対応済み" ? "success" : "warning"}
              variant={label === "対応済み" ? "filled" : "outlined"}
            />
          </Box>
        );
      },
    }] : []),
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => (
        <ActionMenu
          params={params}
          onDelete={handleDelete}
          onRestore={handleRestore}
          onError={handleDeleteError}
          isArchived={isArchivedMode}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">フリー・その他に関する報告書</Typography>

        <Box display={"flex"} justifyContent={"flex-end"} columnGap={1} alignItems="center">
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            sx={{
              width: "400px",
              "& .MuiOutlinedInput-root": {
                height: "40px",
                "& fieldset": {
                  borderColor: "grey.400",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "grey.400",
                },
              },
            }}
            InputProps={{
              placeholder: "入力してEnterを押してください",
              style: { height: "40px" },
            }}
            variant="outlined"
          />
          <Button
            startIcon={<FilterListOutlined />}
            onClick={() => setOpenRefineDialog(true)}
            variant={hasActiveFilters ? "contained" : "outlined"}
            color={hasActiveFilters ? "primary" : "inherit"}
          >
            絞り込み
            {hasActiveFilters && (
              <Chip
                label={Object.values(filterValues).filter(v => v !== "").length}
                size="small"
                sx={{ ml: 1, bgcolor: 'white', color: 'primary.main' }}
              />
            )}
          </Button>
        </Box>
      </Box>

      {/* アーカイブモードと全選択ボタンエリア */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
        {/* アーカイブモード切り替えボタン */}
        <Button
          startIcon={getArchiveIcon()}
          onClick={() => handleArchivedModeChange({ target: { checked: !isArchivedMode } } as React.ChangeEvent<HTMLInputElement>)}
          variant="outlined"
          size="small"
        >
          アーカイブ
        </Button>

        {/* 全選択ボタン（通常モードでのみ表示） */}
        {!isArchivedMode && (
          <Button
            startIcon={getSelectionIcon()}
            onClick={handleTogglePageSelection}
            disabled={reports.length === 0}
            variant="outlined"
            size="small"
          >
            {getSelectionButtonText()}
          </Button>
        )}

        {/* 選択された項目のダウンロードボタン（通常モードでのみ表示） */}
        {!isArchivedMode && selectionModel.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary">
              {selectionModel.length}件選択中
            </Typography>
            <Button
              variant="contained"
              startIcon={<GetApp />}
              onClick={handleDownloadSelectedImages}
              disabled={downloadLoading}
            >
              {downloadLoading ? "ダウンロード中..." : "選択した報告書の画像をダウンロード"}
            </Button>
          </>
        )}
      </Box>

      <DataGrid
        rows={reports ?? []}
        onRowDoubleClick={(params) => {
          navigate(
            `${PATHS.REPORT_FREE_DETAIL.replaceAll(
              ":id",
              params.id.toString()
            )}`
          );
        }}
        columns={listColumns}
        loading={loading}
        sx={{
          cursor: "pointer",
          marginTop: 1,
          '& .MuiDataGrid-columnHeader': {
            paddingLeft: '20px',
          },
        }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slotProps={{
          pagination: {
            labelRowsPerPage: "表示件数:",
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        hideFooterSelectedRowCount
        disableDensitySelector
        disableRowSelectionOnClick
      />

      {/* 共通の絞り込みダイアログ */}
      <ReportFilterDialog
        open={openRefineDialog}
        onClose={() => setOpenRefineDialog(false)}
        onApply={handleFilter}
        onClear={handleClearFilter}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        loading={loading}
      />

      {/* 成功メッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* エラーメッセージ */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* ダウンロード中のローディング */}
      {downloadLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
    </PageContainer>
  );
};

export default ReportFreeList;